export function useOnboardingStateEditor() {
  const responseKey = ENGAGE_LANDING_PROMPT.appStateKey;
  const promptPreferences = [
    [CURRENT_USER_PREFERENCES.onbPeopleDismissed, 'People welcome dismissed'],
    [CURRENT_USER_PREFERENCES.onbDeclutterDismissed, 'Declutter welcome dismissed'],
    [CURRENT_USER_PREFERENCES.onbVideoMergeDismissed, 'Video merge welcome dismissed'],
    [CURRENT_USER_PREFERENCES.onbVideoEditDismissed, 'Video editing welcome dismissed'],
    [CURRENT_USER_PREFERENCES.onbVideoTitleFrameDismissed, 'Video title-frame welcome dismissed']
  ];
  const fields = ref([]);
  const replacing = ref(false);
  let baseline = {};
  let preferenceBaseline = {};

  function load(onboarding = {}, preferences = {}) {
    baseline = JSON.parse(JSON.stringify(onboarding || {}));
    preferenceBaseline = JSON.parse(JSON.stringify(preferences || {}));
    replacing.value = false;
    // Old accounts can still contain flags from the retired Help action list.
    // Do not expose these as working settings; Reset all also clears them.
    const storedKeys = Object.keys(baseline).filter(key =>
      !['people_dismissed', 'declutter_dismissed'].includes(key)
      && !key.startsWith('explore_getting_started.'));
    const keys = [...new Set([responseKey, ...storedKeys])];
    fields.value = [...promptPreferences.map(([key, label]) => {
      const value = preferenceBaseline[key] ?? null;
      return {key, label, source: 'preferences', type: 'boolean', originalValue: value, value, unset: value === null};
    }), ...keys.map(key => {
      const value = baseline[key] ?? null;
      const type = key === responseKey ? 'response'
          : value === null ? null
            : typeof value === 'object' ? 'structured' : typeof value;
      return {
        key,
        label: key === responseKey ? 'Engage landing-page prompt response' : key,
        source: 'app-state',
        type,
        originalValue: value,
        value: type === 'number' ? String(value) : value,
        unset: value === null
      };
    })];
  }

  function valueOf(field) {
    if (field.unset) return null;
    if (field.type === 'number') return String(field.value).trim() === '' ? NaN : Number(field.value);
    return field.value;
  }

  function changed(field) {
    return !Object.is(valueOf(field), field.originalValue);
  }

  function fieldError(field) {
    if (field.unset || (!replacing.value && !changed(field))) return null;
    if (field.type === 'boolean' && typeof field.value !== 'boolean') return 'Choose True, False, or Unset.';
    if (field.type === 'response' && !Object.values(ENGAGE_LANDING_PROMPT.responses).includes(field.value)) {
      return 'Choose Accepted, Declined, or Unset.';
    }
    if (field.type === 'number' && (String(field.value).trim() === '' || !Number.isFinite(Number(field.value)))) {
      return 'Enter a finite number or choose Unset.';
    }
    if (field.type === 'string' && typeof field.value !== 'string') return 'Enter text or choose Unset.';
    return null;
  }

  function setType(field, type) {
    if (!['boolean', 'string', 'number'].includes(type)) return;
    field.type = type;
    field.unset = false;
    field.value = type === 'boolean' ? false : '';
  }

  function setChoice(field, choice) {
    // Preserve an unexpected stored value until the user explicitly changes it.
    if (choice === 'stored') return;
    field.unset = choice === 'unset';
    field.value = field.type === 'boolean' ? choice === 'true' : choice;
  }

  function setInput(field, value) {
    field.value = value;
    field.unset = false;
  }

  function resetAll() {
    replacing.value = true;
    fields.value.forEach(field => { field.unset = true; });
  }

  const dirty = computed(() => replacing.value || fields.value.some(changed));
  const valid = computed(() => fields.value.every(field => !fieldError(field)));

  function submission() {
    if (!valid.value) throw new Error('Invalid onboarding values.');
    return {
      replace: replacing.value,
      changes: Object.fromEntries(fields.value
        .filter(field => field.source === 'app-state')
        .filter(field => replacing.value ? !field.unset : changed(field))
        .map(field => [field.key, valueOf(field)])),
      preferences: Object.fromEntries(fields.value
        .filter(field => field.source === 'preferences' && (replacing.value || changed(field)))
        .map(field => [field.key, valueOf(field)]))
    };
  }

  return {
    fields,
    replacing,
    dirty,
    valid,
    load,
    cancel: () => load(baseline, preferenceBaseline),
    resetAll,
    setType,
    setChoice,
    setInput,
    fieldError,
    submission
  };
}
